import { useQuery } from '@tanstack/react-query';
import { organisationsAPI } from '../../../services/api';
import { orgKeys } from '../../../query/queryKeys';

export function useDashboardCommitsQuery(
  orgId: string | undefined,
  enabled: boolean,
  params: Record<string, unknown>
) {
  return useQuery({
    queryKey: orgKeys.commits(orgId ?? '', params),
    queryFn: () => organisationsAPI.getCommits(orgId!, params),
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  });
}

export function useDashboardPullRequestsQuery(
  orgId: string | undefined,
  enabled: boolean,
  params: Record<string, unknown>
) {
  return useQuery({
    queryKey: orgKeys.pullRequests(orgId ?? '', params),
    queryFn: () => organisationsAPI.getPullRequests(orgId!, params),
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  });
}

export function useDashboardReposForFilter(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: orgKeys.repositories(orgId ?? ''),
    queryFn: async () => {
      const { repositories: list } = await organisationsAPI.getRepositories(orgId!);
      return Array.isArray(list) ? list : [];
    },
    enabled: !!orgId && enabled,
    staleTime: 60_000,
  });
}

/** Map TanStack column id to server commit sort field. */
export function commitSortToServer(columnId: string, desc: boolean) {
  const sortDir = desc ? 'desc' : 'asc';
  switch (columnId) {
    case 'commitFiles':
      return { sortBy: 'changedFiles', sortDir };
    case 'commitAdditions':
      return { sortBy: 'additions', sortDir };
    case 'commitDeletions':
      return { sortBy: 'deletions', sortDir };
    case 'commit':
    default:
      return { sortBy: 'authorDate', sortDir };
  }
}

/** Map TanStack column id to server PR sort field. */
export function prSortToServer(columnId: string, desc: boolean) {
  const sortDir = desc ? 'desc' : 'asc';
  switch (columnId) {
    case 'prFiles':
      return { sortBy: 'changedFiles', sortDir };
    case 'prAdditions':
      return { sortBy: 'additions', sortDir };
    case 'prDeletions':
      return { sortBy: 'deletions', sortDir };
    case 'pr':
    default:
      return { sortBy: 'createdAt', sortDir };
  }
}

/** Churn ratio sorts are client-only on the current page. */
export function isClientOnlyCommitSort(columnId: string) {
  return columnId === 'addDelChurn';
}

export function isClientOnlyPrSort(columnId: string) {
  return columnId === 'prChurn';
}
